import { Button, Stack } from '@prenuvo/halo';

import { FctDialog, Skeleton } from '@/shared/ui';

import { PreferenceLabel } from './preference-label.component';
import { PreferenceRadioGroup } from './preference-radio-group.component';
import { getRadioOptions, getRadioValue } from './preferences.util';
import { usePreferencesModal } from './use-preferences-modal.hook';

interface PreferencesModalProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export function PreferencesModal({ onOpenChange, open }: PreferencesModalProps) {
  const {
    editedValues,
    handleCancel,
    handlePreferenceChange,
    handleReset,
    handleSave,
    isDirty,
    isLoading,
    isPatching,
    isResetting,
    sortedPreferences,
  } = usePreferencesModal(open, onOpenChange);

  const disabled = isPatching || isResetting;

  const handleOpenChange = (value: boolean) => {
    if (disabled) {
      return;
    }

    if (!value) {
      handleCancel();

      return;
    }

    onOpenChange(value);
  };

  return (
    <FctDialog
      className={{ body: 'pt-0' }}
      onOpenChange={handleOpenChange}
      open={open}
      title="FCT.REPORTING.HEADER.PREFERENCES.TITLE"
    >
      <div className="w-full">
        {isLoading ? (
          <Skeleton className="h-10 w-full" count={3} />
        ) : (
          <>
            <Stack variant="vertical">
              {sortedPreferences.map((preference) => (
                <Stack
                  key={preference.key}
                  alignment="center"
                  className="border-b border-neutral-700 py-4 text-sm text-neutral-50 last:border-b-0"
                  gap={4}
                  justification="between"
                >
                  <PreferenceLabel preference={preference} />

                  <PreferenceRadioGroup
                    disabled={disabled}
                    onChange={(val) => handlePreferenceChange(preference.key, val)}
                    options={getRadioOptions(preference)}
                    value={getRadioValue(preference, editedValues[preference.key])}
                  />
                </Stack>
              ))}
            </Stack>

            <Stack alignment="center" className="mt-6" gap={3} isWrapped justification="between">
              <Button
                className={{ root: 'rounded-full' }}
                disabled={disabled}
                onClick={handleReset}
                size="sm"
                translationKey="FCT.REPORTING.HEADER.PREFERENCES.RESET"
                variant="outlined"
              />
              <Stack gap={3} isWrapped>
                <Button
                  className={{ root: 'rounded-full' }}
                  disabled={disabled}
                  onClick={handleCancel}
                  size="sm"
                  translationKey="FCT.REPORTING.HEADER.PREFERENCES.CANCEL"
                  variant="outlined"
                />
                <Button
                  className={{ root: 'rounded-full' }}
                  disabled={!isDirty || disabled}
                  onClick={handleSave}
                  size="sm"
                  translationKey="FCT.REPORTING.HEADER.PREFERENCES.SAVE"
                  variant="primary"
                />
              </Stack>
            </Stack>
          </>
        )}
      </div>
    </FctDialog>
  );
}
